import { useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { SCHOOL_SEAT_PLAN } from "@/constants/plan";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/layouts/PageContainer";
import {
  CreditCard, ChevronLeft, AlertCircle, Plus, X, Minus,
  ExternalLink, CalendarClock, UserMinus, GripVertical, Check,
} from "lucide-react";
import { RemoveSlotModal } from "@/components/modals/RemoveSlotModal";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
}

function getInitials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function RemainingBar({ used, total, label }) {
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.round((remaining / total) * 100) : 100;
  const isLow = pct <= 10;
  const isMid = pct <= 40;
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span>{label}</span>
        <span className={`tabular-nums font-medium ${isLow ? "text-destructive" : "text-foreground"}`}>
          {remaining}{" "}
          <span className="font-normal text-muted-foreground">remaining</span>
        </span>
      </div>
      <div className="h-1 bg-muted/80 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isLow ? "bg-destructive" : isMid ? "bg-warning" : "bg-primary"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function SeatManagement() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [school, setSchool] = useState(null);
  const [schoolSub, setSchoolSub] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddSeats, setShowAddSeats] = useState(false);
  const [addCount, setAddCount] = useState(1);
  const [adding, setAdding] = useState(false);

  const [removeSlotModalOpen, setRemoveSlotModalOpen] = useState(false);
  const [removeCount, setRemoveCount] = useState(1);
  const [removing, setRemoving] = useState(false);

  const [actionInProgress, setActionInProgress] = useState(null);
  const [purchaseCount, setPurchaseCount] = useState(5);
  const [checkingOut, setCheckingOut] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [devPreview, setDevPreview] = useState(false);

  // Drag-and-drop + click-to-assign state
  const [draggingMemberId, setDraggingMemberId] = useState(null);
  const [dragOverSlotIdx, setDragOverSlotIdx] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, membersRes, subRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers, signal }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, { headers, signal }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/subscription`, { headers, signal }),
      ]);
      if (!schoolRes.ok) {
        const body = await schoolRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load school");
      }
      setSchool(await schoolRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (subRes.ok) setSchoolSub(await subRes.json());
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [schoolId, getToken]);

  useAbortableLoader(load);

  const DEV_MOCK_SUB = import.meta.env.DEV ? {
    stripeSubscriptionId: "sub_dev_preview",
    status: "active",
    currentPeriodEnd: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    seats: [
      { _id: "mock-1", userId: members[0]?.userId ?? null, reportsUsed: 47,  aiGensUsed: 130 },
      { _id: "mock-2", userId: members[1]?.userId ?? null, reportsUsed: 112, aiGensUsed: 85  },
      { _id: "mock-3", userId: null,                       reportsUsed: 0,   aiGensUsed: 0   },
    ],
  } : null;

  const effectiveSub = (import.meta.env.DEV && devPreview && !schoolSub?.stripeSubscriptionId)
    ? DEV_MOCK_SUB
    : schoolSub;

  const seats = effectiveSub?.seats ?? [];
  const assignedCount = seats.filter(s => s.userId).length;
  const unassignedCount = seats.length - assignedCount;
  const hasSubscription = !!effectiveSub?.stripeSubscriptionId && effectiveSub?.status !== "none";
  const status = effectiveSub?.status;
  const currentPeriodEnd = effectiveSub?.currentPeriodEnd;
  const fillPct = seats.length ? Math.round((assignedCount / seats.length) * 100) : 0;
  const REPORTS_PER_SEAT = SCHOOL_SEAT_PLAN.reportsPerSeat;
  const AI_PER_SEAT = SCHOOL_SEAT_PLAN.aiCreditsPerSeat;

  const assignedUserIds = new Set(
    seats.map(s => s.userId).filter(Boolean).map(String)
  );
  const getMemberById = (userId) =>
    members.find(m => String(m.userId) === String(userId));

  // ── API actions ───────────────────────────────────────────────────────────────

  const handleAssign = async (idx, userId) => {
    if (!userId) return;
    setActionInProgress(`assign-${idx}`);
    setSelectedMemberId(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/seats/${idx}/assign`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to assign");
      }
      const data = await res.json();
      setSchoolSub(prev => ({ ...prev, seats: data.seats }));
      toast.success("Teacher added to School Plan.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleUnassign = async (idx) => {
    setActionInProgress(`unassign-${idx}`);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/seats/${idx}/assign`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove");
      }
      const data = await res.json();
      setSchoolSub(prev => ({ ...prev, seats: data.seats }));
      toast.success("Teacher removed from School Plan.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAddSeats = async (e) => {
    e.preventDefault();
    const count = Number(addCount);
    if (!count || count < 1) return;
    setAdding(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/seats/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ count }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to add");
      }
      const data = await res.json();
      setSchoolSub(prev => ({ ...prev, seats: data.seats }));
      setAddCount(1);
      setShowAddSeats(false);
      toast.success(`${count} School Plan slot${count !== 1 ? "s" : ""} added.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSeats = async (e) => {
    e?.preventDefault();
    const count = Number(removeCount);
    if (!count || count < 1) return;
    setRemoving(true);
    try {
      const token = await getToken();
      // Backend route is POST /seats/remove
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/seats/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ count }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove");
      }
      const data = await res.json();
      setSchoolSub(prev => ({ ...prev, seats: data.seats }));
      setRemoveCount(1);
      setRemoveSlotModalOpen(false);
      toast.success(`${count} empty slot${count !== 1 ? "s" : ""} removed.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRemoving(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    const count = Number(purchaseCount);
    if (!count || count < 1) return;
    setCheckingOut(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seatCount: count }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create checkout");
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      toast.error(err.message);
      setCheckingOut(false);
    }
  };

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/billing-portal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to open billing portal");
      }
      const { url } = await res.json();
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOpeningPortal(false);
    }
  };

  // ── Drag-and-drop handlers ────────────────────────────────────────────────────

  const onDragStart = (e, memberId) => {
    e.dataTransfer.setData("text/plain", memberId);
    e.dataTransfer.effectAllowed = "move";
    setDraggingMemberId(memberId);
    setSelectedMemberId(null);
  };

  const onDragEnd = () => {
    setDraggingMemberId(null);
    setDragOverSlotIdx(null);
  };

  const onSlotDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverSlotIdx(idx);
  };

  const onSlotDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverSlotIdx(null);
    }
  };

  const onSlotDrop = (e, idx) => {
    e.preventDefault();
    const memberId = e.dataTransfer.getData("text/plain") || draggingMemberId;
    setDraggingMemberId(null);
    setDragOverSlotIdx(null);
    if (memberId) handleAssign(idx, memberId);
  };

  // Click-to-assign: click a teacher to select them, then click an open slot to assign
  const onTeacherClick = (memberId) => {
    setSelectedMemberId(prev => prev === memberId ? null : memberId);
  };

  const onSlotClick = (idx) => {
    if (!selectedMemberId) return;
    handleAssign(idx, selectedMemberId);
  };

  // ── Loading / error states ────────────────────────────────────────────────────

  if (loading) {
    return (
      <PageContainer>
        <div className="h-4 w-28 bg-muted animate-pulse rounded mb-8" />
        <div className="h-8 w-48 bg-muted animate-pulse rounded mb-2" />
        <div className="h-4 w-64 bg-muted animate-pulse rounded mb-8" />
        <div className="h-24 bg-muted animate-pulse rounded-2xl mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            {[0, 1, 2].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}
          </div>
          <div className="space-y-2">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded-xl" />)}
          </div>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  const availableMembers = members.filter(m => !assignedUserIds.has(String(m.userId)));

  // Map memberId → seat index for right-column unassign
  const memberToSeatIdx = {};
  seats.forEach((seat, idx) => {
    if (seat.userId) memberToSeatIdx[String(seat.userId)] = idx;
  });

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="mb-8">
        <h1 className="font-display text-3xl font-normal text-foreground tracking-tight">
          School Plan
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Manage which teachers have access to School Plan features.
        </p>
      </div>

      {import.meta.env.DEV && devPreview && (
        <div className="flex items-center justify-between gap-3 mb-6 px-3 py-2 rounded-lg bg-warning-bg border border-warning-border text-xs text-warning-fg">
          <span>Dev preview - mock subscription data.</span>
          <button onClick={() => setDevPreview(false)} className="underline underline-offset-2 hover:no-underline shrink-0">
            Exit preview
          </button>
        </div>
      )}

      {/* ── Summary card - always shown ─────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-2xl px-5 py-5 mb-8">
        <div className="flex flex-wrap items-start gap-x-8 gap-y-4 mb-4">
          <div className="flex-1 min-w-[180px]">
            <div className="flex items-baseline gap-2 mb-2.5">
              <span className="text-2xl font-semibold tabular-nums text-foreground">
                {hasSubscription ? assignedCount : "-"}
              </span>
              <span className="text-sm text-muted-foreground">
                {hasSubscription
                  ? `of ${seats.length} teachers on School Plan`
                  : "teachers on School Plan"}
              </span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              {hasSubscription && (
                <div
                  className="h-full bg-primary rounded-full transition-all duration-700"
                  style={{ width: `${fillPct}%` }}
                />
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-0.5">Status</p>
              <span className={`text-sm font-semibold ${
                status === "active"   ? "text-success" :
                status === "past_due" ? "text-warning" :
                "text-muted-foreground"
              }`}>
                {status === "active" ? "Active" :
                 status === "past_due" ? "Past Due" :
                 status === "cancelled" ? "Cancelled" : "-"}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-0.5">Renews</p>
              <span className="text-sm font-semibold text-foreground">
                {currentPeriodEnd ? formatDate(currentPeriodEnd) : "-"}
              </span>
            </div>
          </div>

          {hasSubscription && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-8 ml-auto shrink-0"
              onClick={openBillingPortal}
              disabled={openingPortal}
            >
              <ExternalLink size={12} />
              {openingPortal ? "Opening…" : "Billing Portal"}
            </Button>
          )}
        </div>

        {hasSubscription && currentPeriodEnd && (
          <div className="flex items-start gap-2 pt-3.5 border-t border-border">
            <CalendarClock size={12} className="text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Usage resets automatically at renewal. School Plan enrollments carry over - no action needed for the new year.
            </p>
          </div>
        )}
      </div>

      {/* ── Two-column layout - always shown ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

        {/* ── Left: School Plan slots (or purchase prompt) ────────────────── */}
        <div>
          {!hasSubscription ? (
            /* No subscription - purchase prompt */
            <div>
              <div className="mb-3">
                <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground">
                  School Plan
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">No active plan</p>
              </div>

              <div className="bg-card border border-border rounded-2xl px-5 py-5">
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Each School Plan gives one teacher <strong className="text-foreground font-medium">120 reports</strong> and{" "}
                  <strong className="text-foreground font-medium">200 AI generations</strong> per year,
                  billed annually via Stripe.
                </p>

                <form onSubmit={handlePurchase} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Number of teachers</Label>
                    <Input
                      type="number"
                      min={1}
                      value={purchaseCount}
                      onChange={e => setPurchaseCount(e.target.value)}
                      className="h-9 w-28"
                      required
                    />
                  </div>
                  <Button type="submit" disabled={checkingOut || !purchaseCount} className="w-full gap-1.5">
                    <CreditCard size={14} />
                    {checkingOut ? "Redirecting…" : "Purchase School Plan"}
                  </Button>
                </form>

                {import.meta.env.DEV && (
                  <button
                    onClick={() => setDevPreview(true)}
                    className="mt-3 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors w-full text-center"
                  >
                    Dev: preview School Plan view →
                  </button>
                )}
              </div>
            </div>
          ) : (
            /* Has subscription - plan slots */
            <div>
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div>
                  <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground">
                    School Plan
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {unassignedCount > 0
                      ? `${unassignedCount} open slot${unassignedCount !== 1 ? "s" : ""}`
                      : seats.length > 0 ? "All slots filled" : "No slots yet"}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {unassignedCount > 0 && (
                    <Button
                      variant="outline" size="sm"
                      className="gap-1 h-7 text-xs px-2.5"
                      onClick={() => { setRemoveSlotModalOpen(true); setShowAddSeats(false); }}
                    >
                      <Minus size={11} />
                      Remove slot
                    </Button>
                  )}
                  <Button
                    variant="outline" size="sm"
                    className="gap-1 h-7 text-xs px-2.5"
                    onClick={() => { setShowAddSeats(v => !v); }}
                  >
                    <Plus size={11} />
                    Add slot
                  </Button>
                </div>
              </div>

              {showAddSeats && (
                <form onSubmit={handleAddSeats}
                  className="bg-card border border-border rounded-xl px-4 py-3 mb-3 flex items-end gap-3 flex-wrap">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">How many slots to add?</Label>
                    <Input type="number" min={1} value={addCount}
                      onChange={e => setAddCount(e.target.value)}
                      className="h-8 w-20 text-sm" required />
                    <p className="text-xs text-muted-foreground">Prorated for the current billing period.</p>
                  </div>
                  <div className="flex gap-1.5">
                    <Button type="submit" size="sm" disabled={adding} className="h-8 text-xs">
                      {adding ? "Adding…" : "Add"}
                    </Button>
                    <Button type="button" variant="ghost" size="sm" aria-label="Cancel" className="h-8 w-8 p-0"
                      onClick={() => setShowAddSeats(false)}>
                      <X size={13} />
                    </Button>
                  </div>
                </form>
              )}


              {seats.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-14 text-center rounded-2xl border border-dashed border-border">
                  <CreditCard size={22} className="text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No School Plan slots</p>
                  <Button size="sm" variant="outline" className="mt-1 gap-1.5 text-xs h-7"
                    onClick={() => setShowAddSeats(true)}>
                    <Plus size={11} />
                    Add slots
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {seats.map((seat, idx) => {
                    const busy = actionInProgress === `assign-${idx}` || actionInProgress === `unassign-${idx}`;
                    const isAssigned = !!seat.userId;
                    const isDropTarget = dragOverSlotIdx === idx && !isAssigned;
                    const isClickTarget = !!selectedMemberId && !isAssigned;

                    if (isAssigned) {
                      const member = getMemberById(seat.userId);
                      const name = member?.name || member?.email || "Unknown";
                      const email = member?.name ? member?.email : null;
                      const initials = getInitials(name);
                      const reports = seat.reportsUsed ?? 0;
                      const ai = seat.aiGensUsed ?? 0;

                      return (
                        <div key={seat._id}
                          className="bg-muted/20 border border-border rounded-xl p-3 space-y-2.5">
                          {/* Teacher card sits inside the slot */}
                          <div className="bg-card border border-border/50 rounded-lg px-3 py-2.5 flex items-center gap-3 group">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <span className="text-[11px] font-bold text-primary leading-none">{initials}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground truncate leading-snug">{name}</p>
                              {email && <p className="text-xs text-muted-foreground truncate mt-0.5">{email}</p>}
                            </div>
                            <Button
                              variant="ghost" size="sm" disabled={busy}
                              onClick={() => handleUnassign(idx)}
                              className="shrink-0 h-7 px-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              {busy ? "…" : <><UserMinus size={12} className="mr-1" />Remove</>}
                            </Button>
                          </div>
                          {/* Quota bars - remaining */}
                          <div className="space-y-1.5 px-1">
                            <RemainingBar used={reports} total={REPORTS_PER_SEAT} label="Reports" />
                            <RemainingBar used={ai} total={AI_PER_SEAT} label="AI Generations" />
                          </div>
                        </div>
                      );
                    }

                    // Empty slot - outer slot frame + inner drop target + full remaining bars
                    return (
                      <div
                        key={seat._id}
                        className={`rounded-xl border transition-all duration-150 p-3 space-y-2.5 ${
                          isDropTarget  ? "border-primary bg-primary/5" :
                          isClickTarget ? "border-primary/50 bg-primary/5 cursor-pointer" :
                                          "border-dashed border-border bg-muted/10 cursor-default"
                        }`}
                        onDragOver={e => onSlotDragOver(e, idx)}
                        onDragLeave={onSlotDragLeave}
                        onDrop={e => onSlotDrop(e, idx)}
                        onClick={() => onSlotClick(idx)}
                      >
                        {/* Inner teacher card area - empty */}
                        <div className={`rounded-lg border border-dashed flex items-center justify-center min-h-[46px] transition-colors ${
                          isDropTarget  ? "border-primary" :
                          isClickTarget ? "border-primary/50" :
                                          "border-border/50"
                        }`}>
                          {busy ? (
                            <span className="text-xs text-muted-foreground">Assigning…</span>
                          ) : isDropTarget ? (
                            <span className="text-xs font-medium text-primary">Drop to assign</span>
                          ) : isClickTarget ? (
                            <span className="text-xs font-medium text-primary">Click to place here</span>
                          ) : (
                            <span className="text-xs text-muted-foreground/40 text-center px-2">
                              Drag or click a teacher to assign
                            </span>
                          )}
                        </div>
                        {/* Quota bars - all remaining (0 used on empty slot) */}
                        <div className="space-y-1.5 px-1">
                          <RemainingBar used={0} total={REPORTS_PER_SEAT} label="Reports" />
                          <RemainingBar used={0} total={AI_PER_SEAT} label="AI Generations" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right: All teachers - always shown ──────────────────────────── */}
        <div>
          <div className="mb-3">
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground">
              All Teachers
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {!hasSubscription
                ? `${members.length} member${members.length !== 1 ? "s" : ""} in this school`
                : selectedMemberId
                ? "Now click an open slot on the left to assign"
                : availableMembers.length > 0
                ? "Drag or click a teacher to assign them"
                : "All teachers are on School Plan"}
            </p>
          </div>

          {members.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground rounded-xl border border-dashed border-border">
              No school members yet.
            </div>
          ) : (
            <div className="relative">
              <div
                className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1"
                style={{ scrollbarWidth: "thin", scrollbarColor: "hsl(var(--border)) transparent" }}
              >
                {members.map(member => {
                  const memberId = String(member.userId);
                  const isOnPlan = assignedUserIds.has(memberId);
                  const isSelected = selectedMemberId === memberId;
                  const isDraggingThis = draggingMemberId === memberId;
                  const name = member.name || member.email || "Unknown";
                  const email = member.name ? member.email : null;
                  const initials = getInitials(name);
                  const seatIdx = memberToSeatIdx[memberId];
                  const unassignBusy = actionInProgress === `unassign-${seatIdx}`;

                  return (
                    <div
                      key={memberId}
                      draggable={!isOnPlan && hasSubscription}
                      onDragStart={!isOnPlan && hasSubscription ? e => onDragStart(e, memberId) : undefined}
                      onDragEnd={onDragEnd}
                      onClick={() => !isOnPlan && hasSubscription && onTeacherClick(memberId)}
                      className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl border transition-all duration-150 select-none group ${
                        isOnPlan
                          ? "border-border bg-card"
                          : isSelected
                          ? "border-primary bg-primary/5 cursor-pointer ring-1 ring-primary/20"
                          : isDraggingThis
                          ? "border-border bg-card opacity-40 scale-95 cursor-grabbing"
                          : hasSubscription
                          ? "border-border bg-card hover:border-primary/40 hover:bg-muted/20 cursor-grab"
                          : "border-border bg-card cursor-default"
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold leading-none ${
                        isOnPlan ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                      }`}>
                        {initials}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate leading-snug">{name}</p>
                        {email && <p className="text-xs text-muted-foreground truncate">{email}</p>}
                      </div>

                      {isOnPlan ? (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                            On Plan
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={unassignBusy}
                            aria-label="Unassign teacher from seat"
                            onClick={() => handleUnassign(seatIdx)}
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={11} />
                          </Button>
                        </div>
                      ) : isSelected ? (
                        <Check size={14} className="text-primary shrink-0" />
                      ) : hasSubscription ? (
                        <GripVertical size={14} className="text-muted-foreground/30 shrink-0" />
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {members.length > 6 && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-1 h-8 bg-gradient-to-t from-background to-transparent rounded-b-xl" />
              )}
            </div>
          )}
        </div>
      </div>
      <RemoveSlotModal
        open={removeSlotModalOpen}
        onClose={() => setRemoveSlotModalOpen(false)}
        onConfirm={handleRemoveSeats}
        count={removeCount}
        setCount={setRemoveCount}
        max={unassignedCount}
        removing={removing}
      />
    </PageContainer>
  );
}
